import React, { useEffect, useState } from 'react';
import { Horse, useHorse } from '../../contexts/HorseContext';
import { HorseSelector } from '../Weighing/HorseSelector';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';
import { Impressum } from '../Legal/Impressum';
export const StatisticsPage: React.FC = () => {
  const {
    horses
  } = useHorse();
  const [selectedHorse, setSelectedHorse] = useState<Horse | null>(null);
  const [showImpressum, setShowImpressum] = useState(false);
  // Set the first horse as default when component mounts
  useEffect(() => {
    if (horses.length > 0 && !selectedHorse) {
      setSelectedHorse(horses[0]);
    }
  }, [horses, selectedHorse]);
  const toggleImpressum = () => {
    setShowImpressum(!showImpressum);
  };
  if (showImpressum) {
    return <Impressum onBack={toggleImpressum} data-id="element-140" />;
  }
  if (!selectedHorse) {
    return <div className="flex items-center justify-center h-full" data-id="element-141">
        <p className="text-gray-500" data-id="element-142">Keine Pferde verfügbar.</p>
      </div>;
  }
  // Sort weights by date (oldest first) for the chart
  const chartData = [...selectedHorse.weights].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()).map(entry => ({
    date: new Date(entry.date).toLocaleDateString('de-DE'),
    weight: entry.weight
  }));
  // Calculate monthly weight average if enough data
  const monthlyData = chartData.reduce((acc, curr) => {
    const date = new Date(curr.date);
    const monthYear = `${date.getMonth() + 1}/${date.getFullYear()}`;
    if (!acc[monthYear]) {
      acc[monthYear] = {
        weights: [],
        month: monthYear
      };
    }
    acc[monthYear].weights.push(curr.weight);
    return acc;
  }, {} as Record<string, {
    weights: number[];
    month: string;
  }>);
  const monthlyAverages = Object.values(monthlyData).map(data => ({
    month: data.month,
    average: Math.round(data.weights.reduce((sum, w) => sum + w, 0) / data.weights.length)
  }));
  // Calculate statistics
  const allWeights = selectedHorse.weights.map(w => w.weight);
  const minWeight = Math.min(...allWeights);
  const maxWeight = Math.max(...allWeights);
  const avgWeight = Math.round(allWeights.reduce((sum, w) => sum + w, 0) / allWeights.length);
  // Count marks by type (simple categorization)
  const markCategories = selectedHorse.marks.reduce((acc, mark) => {
    const lowerDesc = mark.description.toLowerCase();
    if (lowerDesc.includes('verletzung') || lowerDesc.includes('wunde')) {
      acc.injuries = (acc.injuries || 0) + 1;
    } else if (lowerDesc.includes('narbe')) {
      acc.scars = (acc.scars || 0) + 1;
    } else if (lowerDesc.includes('fleck')) {
      acc.marks = (acc.marks || 0) + 1;
    } else {
      acc.other = (acc.other || 0) + 1;
    }
    return acc;
  }, {} as Record<string, number>);
  const markData = Object.entries(markCategories).map(([category, count]) => ({
    category: category === 'injuries' ? 'Verletzungen' : category === 'scars' ? 'Narben' : category === 'marks' ? 'Flecken' : 'Sonstiges',
    count
  }));
  return <div className="w-full max-w-full" data-id="element-143">
      <div className="flex justify-between items-center mb-4" data-id="element-144">
        <div className="flex items-center" data-id="element-145">
          <h1 className="text-2xl font-bold" data-id="element-146">Pferd Statistik</h1>
          <button onClick={toggleImpressum} className="ml-4 text-xs text-gray-600 hover:text-gray-900 hover:underline" data-id="element-147">
            Impressum
          </button>
        </div>
        <div className="w-64" data-id="element-148">
          <HorseSelector horses={horses} selectedHorse={selectedHorse} onSelectHorse={setSelectedHorse} data-id="element-149" />
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-150">
        {/* Weight Overview */}
        <div className="bg-white rounded-lg shadow p-6 mb-4" data-id="element-151">
          <h2 className="text-xl font-semibold mb-4" data-id="element-152">Gewichtsverlauf</h2>
          <div className="grid grid-cols-3 gap-4 mb-6" data-id="element-153">
            <div className="bg-[#284755] p-3 rounded-lg text-center" data-id="element-154">
              <p className="text-sm text-white" data-id="element-155">Min</p>
              <p className="text-xl font-bold text-[#96c11f]" data-id="element-156">{minWeight} kg</p>
            </div>
            <div className="bg-[#284755] p-3 rounded-lg text-center" data-id="element-157">
              <p className="text-sm text-white" data-id="element-158">Durchschnitt</p>
              <p className="text-xl font-bold text-[#96c11f]" data-id="element-159">{avgWeight} kg</p>
            </div>
            <div className="bg-[#284755] p-3 rounded-lg text-center" data-id="element-160">
              <p className="text-sm text-white" data-id="element-161">Max</p>
              <p className="text-xl font-bold text-[#96c11f]" data-id="element-162">{maxWeight} kg</p>
            </div>
          </div>
          {chartData.length > 0 ? <div className="h-64 w-full" data-id="element-163">
              <ResponsiveContainer width="100%" height="100%" data-id="element-164">
                <LineChart data={chartData} margin={{
              top: 5,
              right: 20,
              left: 10,
              bottom: 5
            }} data-id="element-165">
                  <CartesianGrid strokeDasharray="3 3" data-id="element-166" />
                  <XAxis dataKey="date" data-id="element-167" />
                  <YAxis domain={[minWeight - 10, maxWeight + 10]} label={{
                value: 'kg',
                angle: -90,
                position: 'insideLeft'
              }} data-id="element-168" />
                  <Tooltip formatter={value => [`${value} kg`, 'Gewicht']} data-id="element-169" />
                  <Line type="monotone" dataKey="weight" stroke="#96c11f" strokeWidth={2} dot={{
                r: 4
              }} activeDot={{
                r: 6
              }} data-id="element-170" />
                </LineChart>
              </ResponsiveContainer>
            </div> : <p className="text-center text-gray-500" data-id="element-171">
              Keine Gewichtsdaten vorhanden
            </p>}
        </div>
        {/* Monthly Averages */}
        <div className="bg-white rounded-lg shadow p-6 mb-4" data-id="element-172">
          <h2 className="text-xl font-semibold mb-4" data-id="element-173">
            Monatliche Durchschnitte
          </h2>
          {monthlyAverages.length > 1 ? <div className="h-64 w-full" data-id="element-174">
              <ResponsiveContainer width="100%" height="100%" data-id="element-175">
                <BarChart data={monthlyAverages} margin={{
              top: 5,
              right: 20,
              left: 10,
              bottom: 5
            }} data-id="element-176">
                  <CartesianGrid strokeDasharray="3 3" data-id="element-177" />
                  <XAxis dataKey="month" data-id="element-178" />
                  <YAxis domain={[minWeight - 10, maxWeight + 10]} label={{
                value: 'kg',
                angle: -90,
                position: 'insideLeft'
              }} data-id="element-179" />
                  <Tooltip formatter={value => [`${value} kg`, 'Durchschnitt']} data-id="element-180" />
                  <Bar dataKey="average" fill="#96c11f" data-id="element-181" />
                </BarChart>
              </ResponsiveContainer>
            </div> : <p className="text-center text-gray-500" data-id="element-182">
              Nicht genügend Daten für monatliche Durchschnitte
            </p>}
        </div>
        {/* Marks & Injuries */}
        <div className="bg-white rounded-lg shadow p-6 mb-4" data-id="element-183">
          <h2 className="text-xl font-semibold mb-4" data-id="element-184">
            Merkmale & Verletzungen
          </h2>
          {selectedHorse.marks.length > 0 ? <>
              <div className="relative border border-gray-200 rounded-lg mb-4 h-64 bg-gray-50 flex items-center justify-center" data-id="element-185">
                {/* Horse silhouette as background */}
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/tARazP8X4w5FUECvV8zKt5/ChatGPT_Image_25._Sept._2025%2C_11_24_34.png" alt="Pferd Anatomie" className="max-h-full object-contain opacity-40" data-id="element-186" />
                {/* Display marks on the silhouette */}
                {selectedHorse.marks.map(mark => <div key={mark.id} className="absolute w-5 h-5 transform -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white cursor-pointer" style={{
              left: `${mark.x}%`,
              top: `${mark.y}%`,
              backgroundColor: mark.description.toLowerCase().includes('verletzung') || mark.description.toLowerCase().includes('wunde') ? '#ef4444' : mark.description.toLowerCase().includes('narbe') ? '#8b5cf6' : '#f59e0b'
            }} title={mark.description} data-id="element-187" />)}
                {/* Legend */}
                <div className="absolute bottom-2 right-2 bg-white bg-opacity-75 p-2 rounded-md text-xs" data-id="element-188">
                  <div className="flex items-center mb-1" data-id="element-189">
                    <div className="w-3 h-3 rounded-full bg-red-500 mr-2" data-id="element-190"></div>
                    <span data-id="element-191">Verletzungen</span>
                  </div>
                  <div className="flex items-center mb-1" data-id="element-192">
                    <div className="w-3 h-3 rounded-full bg-purple-500 mr-2" data-id="element-193"></div>
                    <span data-id="element-194">Narben</span>
                  </div>
                  <div className="flex items-center" data-id="element-195">
                    <div className="w-3 h-3 rounded-full bg-amber-500 mr-2" data-id="element-196"></div>
                    <span data-id="element-197">Andere Merkmale</span>
                  </div>
                </div>
              </div>
              <div className="mt-4" data-id="element-198">
                <h3 className="font-medium mb-2" data-id="element-199">Alle Merkmale:</h3>
                <div className="max-h-40 overflow-y-auto border rounded-md" data-id="element-200">
                  <table className="min-w-full divide-y divide-gray-200" data-id="element-201">
                    <thead className="bg-gray-50 sticky top-0" data-id="element-202">
                      <tr data-id="element-203">
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500" data-id="element-204">
                          Datum
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500" data-id="element-205">
                          Beschreibung
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500" data-id="element-206">
                          Typ
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200" data-id="element-207">
                      {selectedHorse.marks.sort((a, b) => {
                    // Sort by date (newest first)
                    const dateA = a.date.split('.').reverse().join('-');
                    const dateB = b.date.split('.').reverse().join('-');
                    return new Date(dateB).getTime() - new Date(dateA).getTime();
                  }).map(mark => {
                    // Determine type
                    let type = 'Merkmal';
                    let typeColor = 'bg-amber-100 text-amber-800';
                    if (mark.description.toLowerCase().includes('verletzung') || mark.description.toLowerCase().includes('wunde')) {
                      type = 'Verletzung';
                      typeColor = 'bg-red-100 text-red-800';
                    } else if (mark.description.toLowerCase().includes('narbe')) {
                      type = 'Narbe';
                      typeColor = 'bg-purple-100 text-purple-800';
                    }
                    return <tr key={mark.id} className="hover:bg-gray-50" data-id="element-208">
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-209">
                                {mark.date}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-900" data-id="element-210">
                                {mark.description}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm" data-id="element-211">
                                <span className={`px-2 py-1 text-xs rounded-full ${typeColor}`} data-id="element-212">
                                  {type}
                                </span>
                              </td>
                            </tr>;
                  })}
                    </tbody>
                  </table>
                </div>
              </div>
            </> : <p className="text-center text-gray-500" data-id="element-213">
              Keine Merkmale oder Verletzungen vorhanden
            </p>}
        </div>
        {/* Horse Info */}
        <div className="bg-white rounded-lg shadow p-6 mb-4" data-id="element-214">
          <h2 className="text-xl font-semibold mb-4" data-id="element-215">Pferdedaten</h2>
          <div className="grid grid-cols-2 gap-4" data-id="element-216">
            <div data-id="element-217">
              <p className="text-sm text-gray-500" data-id="element-218">Name</p>
              <p className="font-medium" data-id="element-219">{selectedHorse.name}</p>
            </div>
            <div data-id="element-220">
              <p className="text-sm text-gray-500" data-id="element-221">Geburtsjahr</p>
              <p className="font-medium" data-id="element-222">{selectedHorse.birthYear}</p>
            </div>
            <div data-id="element-223">
              <p className="text-sm text-gray-500" data-id="element-224">Rasse</p>
              <p className="font-medium" data-id="element-225">{selectedHorse.breed || '-'}</p>
            </div>
            <div data-id="element-226">
              <p className="text-sm text-gray-500" data-id="element-227">Abstammung</p>
              <p className="font-medium" data-id="element-228">{selectedHorse.lineage || '-'}</p>
            </div>
            <div data-id="element-229">
              <p className="text-sm text-gray-500" data-id="element-230">Anzahl Verwiegungen</p>
              <p className="font-medium" data-id="element-231">{selectedHorse.weights.length}</p>
            </div>
            <div data-id="element-232">
              <p className="text-sm text-gray-500" data-id="element-233">Anzahl Merkmale</p>
              <p className="font-medium" data-id="element-234">{selectedHorse.marks.length}</p>
            </div>
          </div>
        </div>
      </div>
    </div>;
};